import { memo, useCallback, useEffect, useMemo, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import {
  DOLGU, TUMU_KAPALI, genislikler, sigdir, grupAcikMi, yaziGenisligi, grupCevir, gruplaSatirlar, hucreMetni, kolonToplami, sayisalMi,
  siralaSatirlar, toplamEtiketi as etiketHesapla,
  type Grup, type GrupAcikligi, type GrupAyari, type HucreDegeri, type RaporKolonu, type Siralama,
} from './kolon';
import type { GorunumProps } from './gorunum';
import { KartGorunumu } from './RaporKartlari';
import { useDarEkran } from './useDarEkran';

/**
 * S334: sabit toplamlı rapor tablosu. Başlık üstte, toplam satırı altta YAPIŞIK; tablo kendisine
 * verilen yüksekliği doldurur ve KENDİ içinde kayar (sayfa kaymaz). Stiller `styles/rapor-ekrani.css`.
 *
 * BÜYÜK VERİ: satırlar SANALLAŞTIRILIR (bir firmada 192 bin stok kartı var). DOM'a yalnız görünen
 * pencere + tampon basılır; toplam, sıralama ve gruplama her zaman TÜM `satirlar` üzerinden hesaplanır.
 * Sayfalama seçilmedi: gruplar ve ara toplamlar sayfa sınırında bölünür, "listenin tamamı" hissi
 * (kaydırarak tarama, başlıkla sıralama) kaybolurdu. Sanallaştırma sabit satır yüksekliği ister, bu
 * yüzden hücreler tek satırdır (taşan metin üç noktayla kesilir, tamamı `title`da).
 *
 * `table-layout: fixed` + `<colgroup>`: otomatik yerleşimde kolon genişliği yalnız DOM'daki satırlardan
 * ölçülür ve kaydırdıkça kolonlar oynardı. Genişlik TÜM satırlardan bir kez hesaplanır (`kolon.genislikler`).
 *
 * TELEFON (<= 767 px, `useDarEkran`): tablo yerine KART kipi (`RaporKartlari.tsx`). Model (sıralama,
 * gruplama, toplam, etiket) burada bir kez hesaplanır, iki görünüm aynısını çizer (`gorunum.ts`).
 */
export const SATIR_YUKSEKLIGI = 32;
/** Pencerenin üstünde ve altında fazladan basılan satır: hızlı kaydırmada boş şerit görünmesin. */
const TAMPON = 12;
/** Kap ölçülemezse (jsdom, ilk kare) varsayılan görünür yükseklik. */
const VARSAYILAN_YUKSEKLIK = 640;
// Her render'da yeni `[]` üretmemek için (memo bağımlılıkları sabit kalsın).
const BOS: readonly never[] = [];
const ETIKET_YAZISI = '700 13px "Inter Tight", system-ui, sans-serif';

export interface RaporTablosuProps<S> {
  kolonlar: readonly RaporKolonu<S>[];
  /** SÜZÜLMÜŞ satırların tamamı. Toplam bunların hepsinden hesaplanır. */
  satirlar: readonly S[];
  satirKimligi: (satir: S) => string | number;
  /** En üstte duran, sıralamaya ve toplama GİRMEYEN satırlar ("Devreden"). İtalik basılır. */
  ustSatirlar?: readonly S[];
  onSatirTikla?: (satir: S) => void;
  seciliKimlik?: string | number | null;
  grup?: GrupAyari<S> | null;
  /** Verilirse grup açıklığı dışarıdan yönetilir ("Tümünü aç / kapat" düğmeleri filtre barında). */
  grupAcikligi?: GrupAcikligi;
  onGrupAcikligi?: (a: GrupAcikligi) => void;
  /** Verilirse sıralama dışarıdan yönetilir. null: verinin geldiği sıra. */
  siralama?: Siralama | null;
  onSiralama?: (s: Siralama | null) => void;
  /** Süzgeçsiz etiket. Varsayılan "Genel Toplam". */
  toplamEtiketi?: string;
  /** Süzgeçten ÖNCEKİ satır sayısı; `satirlar` bundan azsa etiket "Süzülen toplam (N satır)" olur. */
  hamSatirSayisi?: number;
  /** Satır sayısı azalmadan daralan süzgeç (sunucu süzgeci) etkinse `true`. */
  suzuldu?: boolean;
  bosMesaj?: string;
  /** İlk yükleme: iskelet satırlar. */
  yukleniyor?: boolean;
  /** Önceki veri ekrandayken yenisi geliyor: tablo durur, soluklaşır, `aria-busy`. */
  yenileniyor?: boolean;
  testid?: string;
  /** Ekran okuyucu için tablo adı. */
  etiket?: string;
  className?: string;
  /**
   * Sunum kipi. 'oto' (varsayılan): telefonda kart, ötesinde tablo. Testte ve bilinçli seçimde açıkça
   * verilir (jsdom yerleşim ölçmez; duyarlı karar orada sınanamaz).
   */
  kip?: 'oto' | 'tablo' | 'kart';
  /** Kart kipinde araç satırının solundaki yer: arama girişi. Sağında "Sırala" durur. */
  kartAraclari?: ReactNode;
  /** Kart kipinde görünüm: 'kutu' (varsayılan) ayrı kartlar, 'cizgi' çizgiyle ayrılmış satırlar. */
  kartStili?: 'kutu' | 'cizgi';
}

type Oge<S> =
  | { tur: 'ust'; satir: S }
  | { tur: 'grup'; grup: Grup<S>; acik: boolean }
  | { tur: 'satir'; satir: S; ic: boolean };

const hizaSinifi = <S,>(k: RaporKolonu<S>) => {
  const h = k.hizala ?? (sayisalMi(k.tur) ? 'sag' : 'sol');
  return h === 'sag' ? ' rt-sag' : h === 'orta' ? ' rt-orta' : '';
};
const hucreSinifi = <S,>(k: RaporKolonu<S>) =>
  hizaSinifi(k) + (sayisalMi(k.tur) ? ' rt-sayi' : '') + (k.tur === 'kod' ? ' rt-kod' : '');

interface SatirProps<S> {
  satir: S;
  kolonlar: readonly RaporKolonu<S>[];
  tek: boolean;
  ic: boolean;
  ust: boolean;
  secili: boolean;
  onTikla?: (satir: S) => void;
}

function SatirHam<S>({ satir, kolonlar, tek, ic, ust, secili, onTikla }: SatirProps<S>) {
  const tiklanir = !!onTikla && !ust;
  return (
    <tr
      className={'rt-satir' + (tek ? ' rt-tek' : '') + (ust ? ' rt-ust' : '') + (tiklanir ? ' rt-tiklanir' : '') + (ic ? ' rt-ic' : '')}
      aria-selected={tiklanir ? secili : undefined}
      tabIndex={tiklanir ? 0 : undefined}
      onClick={tiklanir ? () => onTikla(satir) : undefined}
      onKeyDown={tiklanir ? (e: KeyboardEvent) => { if (e.key === 'Enter') onTikla(satir); } : undefined}
    >
      {kolonlar.map((k) => {
        const v = k.deger(satir);
        const metin = hucreMetni(k.tur, v);
        return (
          <td key={k.id} className={hucreSinifi(k)} title={k.tur === 'metin' && metin ? metin : undefined}>
            {k.hucre ? k.hucre(v) : metin}
          </td>
        );
      })}
    </tr>
  );
}
const Satir = memo(SatirHam) as typeof SatirHam;

export function RaporTablosu<S>({
  kolonlar: tumKolonlar, satirlar, satirKimligi, ustSatirlar, onSatirTikla, seciliKimlik,
  grup, grupAcikligi, onGrupAcikligi, siralama: disSiralama, onSiralama,
  toplamEtiketi = 'Genel Toplam', hamSatirSayisi, suzuldu = false,
  bosMesaj = 'Bu süzgeçle kayıt yok.', yukleniyor = false, yenileniyor = false,
  testid = 'rapor-tablosu', etiket, className = '',
  kip = 'oto', kartAraclari, kartStili = 'kutu',
}: RaporTablosuProps<S>) {
  const darEkran = useDarEkran();
  const kart = kip === 'kart' || (kip === 'oto' && darEkran);
  const ust = ustSatirlar ?? BOS;
  // Gruplanan alan kolon olarak gösterilmez: değeri grup satırında yazıyor.
  const kolonlar = useMemo(
    () => (grup ? tumKolonlar.filter((k) => k.id !== grup.id) : tumKolonlar),
    [tumKolonlar, grup],
  );

  // ── sıralama: dışarıdan verilmediyse içeride ─────────────────────────────────
  const [icSiralama, setIcSiralama] = useState<Siralama | null>(null);
  const siralama = disSiralama !== undefined ? disSiralama : icSiralama;
  const siralaDegis = useCallback((yeni: Siralama | null) => {
    if (disSiralama === undefined) setIcSiralama(yeni);
    onSiralama?.(yeni);
  }, [disSiralama, onSiralama]);

  // ── grup açıklığı: dışarıdan verilmediyse içeride; gruplama alanı değişince hepsi kapanır ──
  const [icAciklik, setIcAciklik] = useState<GrupAcikligi>(TUMU_KAPALI);
  const grupId = grup?.id ?? null;
  const [oncekiGrupId, setOncekiGrupId] = useState(grupId);
  if (oncekiGrupId !== grupId) {
    setOncekiGrupId(grupId);
    setIcAciklik(TUMU_KAPALI);
  }
  const aciklik = grupAcikligi ?? icAciklik;
  const grubuCevir = useCallback((ad: string) => {
    const yeni = grupCevir(aciklik, ad);
    if (grupAcikligi === undefined) setIcAciklik(yeni);
    onGrupAcikligi?.(yeni);
  }, [aciklik, grupAcikligi, onGrupAcikligi]);
  const grupAcik = useCallback((ad: string) => grupAcikMi(aciklik, ad), [aciklik]);

  const sirali = useMemo(() => siralaSatirlar(satirlar, kolonlar, siralama), [satirlar, kolonlar, siralama]);
  const gruplar = useMemo(
    () => (grup ? gruplaSatirlar(sirali, grup, kolonlar, siralama) : null),
    [sirali, grup, kolonlar, siralama],
  );
  // Ara toplam bir kez hesaplanır: kaydırırken ya da kart açarken yeniden toplanmaz.
  const araToplamlar = useMemo(
    () => new Map((gruplar ?? []).map((g) => [g.ad, kolonlar.map((k) => kolonToplami(k, g.satirlar))])),
    [gruplar, kolonlar],
  );
  const toplamVar = kolonlar.some((k) => k.toplam);
  // 🔴 Toplam `satirlar`ın TAMAMINDAN: DOM'a basılan pencereden (tablo) ya da sayfadan (kart) değil.
  const toplamlar = useMemo(
    () => (toplamVar ? kolonlar.map((k) => kolonToplami(k, sirali)) : null),
    [toplamVar, kolonlar, sirali],
  );
  const etiketMetni = etiketHesapla(toplamEtiketi, satirlar.length, hamSatirSayisi ?? satirlar.length, suzuldu);

  const model: GorunumProps<S> = {
    kolonlar, sirali, ust, gruplar, grup: grup ?? null, grupAcik, grubuCevir, araToplamlar, toplamlar, etiketMetni,
    siralama, siralaDegis, satirKimligi, onSatirTikla, seciliKimlik, bosMesaj, yukleniyor, yenileniyor,
    testid, etiket, className, kartAraclari, kartStili,
  };
  // İki görünüm AYRI bileşen: kancaları farklı, kip değişince (pencere 767'yi geçince) biri sökülür.
  return kart ? <KartGorunumu {...model} /> : <TabloGorunumu {...model} />;
}

function TabloGorunumu<S>({
  kolonlar, sirali, ust, gruplar, grup, grupAcik, grubuCevir, araToplamlar, toplamlar, etiketMetni,
  siralama, siralaDegis, satirKimligi, onSatirTikla, seciliKimlik, bosMesaj, yukleniyor, yenileniyor,
  testid, etiket, className,
}: GorunumProps<S>) {
  const sirala = (id: string) =>
    siralaDegis(siralama?.id === id ? { id, yon: siralama.yon === 1 ? -1 : 1 } : { id, yon: 1 });
  const toplamVar = toplamlar !== null;

  // Gruplu görünümde düz öğe dizisi (grup satırı + açık grubun satırları). Grupsuzda dizi KURULMAZ:
  // 192 bin satır için 192 bin sarmalayıcı nesne üretmek yerine `ogeAl` doğrudan indeksler.
  const duz = useMemo(() => {
    if (!gruplar) return null;
    const d: Oge<S>[] = ust.map((satir) => ({ tur: 'ust', satir }));
    for (const g of gruplar) {
      const acik = grupAcik(g.ad);
      d.push({ tur: 'grup', grup: g, acik });
      if (acik) for (const satir of g.satirlar) d.push({ tur: 'satir', satir, ic: true });
    }
    return d;
  }, [gruplar, ust, grupAcik]);
  const ogeSayisi = duz ? duz.length : ust.length + sirali.length;
  const ogeAl = (i: number): Oge<S> =>
    duz ? duz[i] : i < ust.length ? { tur: 'ust', satir: ust[i] } : { tur: 'satir', satir: sirali[i - ust.length], ic: false };

  // ── sanal pencere ──────────────────────────────────────────────────────────
  const kapRef = useRef<HTMLDivElement | null>(null);
  const [ilkGorunen, setIlkGorunen] = useState(0);
  const [kapYuksekligi, setKapYuksekligi] = useState(0);
  // Kolonları kaba sığdırmak için (bkz. `kolon.sigdir`). jsdom'da 0: sığdırma yapılmaz.
  const [kapGenisligi, setKapGenisligi] = useState(0);
  useEffect(() => {
    const kap = kapRef.current;
    if (!kap) return;
    const olc = () => { setKapYuksekligi(kap.clientHeight); setKapGenisligi(kap.clientWidth); };
    olc();
    const ro = new ResizeObserver(olc);
    ro.observe(kap);
    return () => ro.disconnect();
  }, []);
  const gorunenSatir = Math.ceil((kapYuksekligi || VARSAYILAN_YUKSEKLIK) / SATIR_YUKSEKLIGI);
  const bas = Math.max(0, Math.min(ilkGorunen - TAMPON, ogeSayisi - gorunenSatir - TAMPON));
  const son = Math.min(ogeSayisi, bas + gorunenSatir + 2 * TAMPON);

  const kolonGenislikleri = useMemo(
    () => genislikler(kolonlar, sirali, ust, !!grup),
    [kolonlar, sirali, ust, grup],
  );
  // Toplam etiketi ve grup adı ilk toplanan kolona kadar uzanır (en az bir kolon).
  const etiketKolonu = Math.max(1, kolonlar.findIndex((k) => k.toplam));
  // Esnek kolon artan yeri alır; `<col>`una genişlik yazılmaz (en az genişliği tablonun `min-width`inde).
  const esnek = useMemo(() => {
    const secilen = kolonlar.findIndex((k) => k.esnek);
    return secilen >= 0 ? secilen : kolonlar.findIndex((k) => k.tur === 'metin' && !k.genislik && !k.hucre);
  }, [kolonlar]);
  const genislik = useMemo(() => {
    const g = kolonGenislikleri.slice();
    // Etiket kendi hücrelerine sığmalı: sığmıyorsa ilk kolon genişler.
    const etiketPx = Math.ceil(yaziGenisligi(etiketMetni, ETIKET_YAZISI) + DOLGU);
    const mevcut = g.slice(0, etiketKolonu).reduce((t, x) => t + x, 0);
    if (toplamVar && mevcut < etiketPx && g.length > 0) g[0] += etiketPx - mevcut;
    return sigdir(g, kolonlar, esnek, kapGenisligi);
  }, [kolonGenislikleri, etiketMetni, etiketKolonu, toplamVar, kolonlar, esnek, kapGenisligi]);
  const enAz = genislik.reduce((t, x) => t + x, 0);

  const araToplamHucresi = (k: RaporKolonu<S>, v: HucreDegeri): ReactNode => (k.hucre ? k.hucre(v) : hucreMetni(k.tur, v));

  const govde: ReactNode[] = [];
  if (!yukleniyor) {
    if (bas > 0) govde.push(<tr key="rt-ust-bosluk" aria-hidden="true" className="rt-bosluk"><td colSpan={kolonlar.length} style={{ height: bas * SATIR_YUKSEKLIGI }} /></tr>);
    for (let i = bas; i < son; i++) {
      const o = ogeAl(i);
      if (o.tur === 'grup') {
        const g = o.grup;
        govde.push(
          <tr
            key={`g:${g.ad}`}
            className="rt-satir rt-grup rt-tiklanir"
            data-grup={g.ad}
            aria-expanded={o.acik}
            tabIndex={0}
            onClick={() => grubuCevir(g.ad)}
            onKeyDown={(e) => { if (e.key === 'Enter') grubuCevir(g.ad); }}
          >
            <td colSpan={etiketKolonu} title={g.ad}>
              <span className="rt-grup-ok" aria-hidden="true">{o.acik ? '▾' : '▸'}</span>
              {g.ad}
              <span className="rt-grup-say">{g.satirlar.length}{grup?.birim ? ` ${grup.birim}` : ''}</span>
            </td>
            {kolonlar.slice(etiketKolonu).map((k, ki) => (
              <td key={k.id} className={hucreSinifi(k)}>
                {k.toplam ? araToplamHucresi(k, araToplamlar.get(g.ad)?.[ki + etiketKolonu]) : null}
              </td>
            ))}
          </tr>,
        );
      } else {
        const kimlik = o.tur === 'ust' ? `u:${i}` : satirKimligi(o.satir);
        govde.push(
          <Satir
            key={kimlik}
            satir={o.satir}
            kolonlar={kolonlar}
            tek={i % 2 === 1}
            ic={o.tur === 'satir' && o.ic}
            ust={o.tur === 'ust'}
            secili={o.tur === 'satir' && seciliKimlik != null && String(seciliKimlik) === String(kimlik)}
            onTikla={onSatirTikla}
          />,
        );
      }
    }
    if (son < ogeSayisi) govde.push(<tr key="rt-alt-bosluk" aria-hidden="true" className="rt-bosluk"><td colSpan={kolonlar.length} style={{ height: (ogeSayisi - son) * SATIR_YUKSEKLIGI }} /></tr>);
  } else {
    for (let i = 0; i < 8; i++) {
      govde.push(
        <tr key={i} className="rt-satir" data-iskelet="true">
          {kolonlar.map((k, ki) => (
            <td key={k.id}><div className="skeleton h-3 rounded" style={{ width: `${45 + ((i + ki) % 4) * 12}%` }} /></td>
          ))}
        </tr>,
      );
    }
  }

  return (
    <div
      ref={kapRef}
      data-testid={testid}
      data-yenileniyor={yenileniyor || undefined}
      aria-busy={yukleniyor || yenileniyor}
      className={`rt-kap thin-scroll ${className}`}
      tabIndex={0}
      onScroll={(e) => setIlkGorunen(Math.floor(e.currentTarget.scrollTop / SATIR_YUKSEKLIGI))}
    >
      <table className="rt-tablo" style={{ minWidth: enAz }} aria-label={etiket} aria-rowcount={yukleniyor ? undefined : ogeSayisi}>
        <colgroup>
          {kolonlar.map((k, i) => (
            <col key={k.id} style={i === esnek ? undefined : { width: genislik[i] }} />
          ))}
        </colgroup>
        <thead>
          <tr>
            {kolonlar.map((k) => {
              const yon = siralama?.id === k.id ? siralama.yon : 0;
              return (
                <th
                  key={k.id}
                  scope="col"
                  className={hizaSinifi(k)}
                  aria-sort={yon === 1 ? 'ascending' : yon === -1 ? 'descending' : 'none'}
                >
                  <button type="button" className="rt-baslik" onClick={() => sirala(k.id)}>
                    {k.baslik}
                    {yon !== 0 && <span className="rt-ok" aria-hidden="true">{yon === 1 ? '▲' : '▼'}</span>}
                  </button>
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>{govde}</tbody>
        {toplamlar && !yukleniyor && (
          <tfoot>
            <tr data-testid={`${testid}-toplam`}>
              <td colSpan={etiketKolonu} title={etiketMetni}>{etiketMetni}</td>
              {kolonlar.slice(etiketKolonu).map((k, i) => (
                <td key={k.id} className={hucreSinifi(k)}>
                  {k.toplam ? araToplamHucresi(k, toplamlar[i + etiketKolonu]) : null}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
      {!yukleniyor && ogeSayisi === 0 && (
        <div className="rt-bos" data-testid={`${testid}-bos`}>{bosMesaj}</div>
      )}
    </div>
  );
}
